import React, { useEffect, useMemo, useState } from "react";
import { X, ChevronLeft, ChevronRight, Circle, Network } from "lucide-react";
import { Button } from "./ui/button";
import { api } from "../services/api";
import { useNavigate } from "react-router-dom";
import CategoryIcon, { getCategoryIconUrl } from "./CategoryIcon";

function navPath(cat) {
  if (!cat?.slug) return "/";
  return Number(cat.level) === 1 ? `/category/${cat.slug}` : `/subcategory/${cat.slug}`;
}

const hasChildren = (cat) => Array.isArray(cat?.children) && cat.children.length > 0;
const firstChild = (cat) => (Array.isArray(cat?.children) ? cat.children[0] || null : null);

function firstPath(cat) {
  const l2 = firstChild(cat);
  const l3 = firstChild(l2);
  const l4 = firstChild(l3);
  const l5 = firstChild(l4);
  return { l2, l3, l4, l5 };
}

function MenuItem({ active, title, hasNext, child, expanded, category, onEnter, onClick }) {
  const hasUploadedIcon = Boolean(getCategoryIconUrl(category));

  return (
    <button
      type="button"
      onMouseEnter={onEnter}
      onFocus={onEnter}
      onClick={onClick}
      className={[
        "group flex w-full items-center gap-3 rounded-xl px-3 py-3 text-right transition",
        child ? "border border-slate-100 bg-slate-50/70 py-2.5 pr-5" : "",
        active ? "bg-blue-50 text-[#005bbb] shadow-sm" : "text-slate-700 hover:bg-blue-50 hover:text-[#005bbb]",
      ].join(" ")}
    >
      <span
        className={[
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-xl",
          active ? "bg-white text-[#005bbb]" : "bg-slate-100 text-slate-400 group-hover:bg-white",
        ].join(" ")}
      >
        <CategoryIcon
          category={category}
          fallback={<Circle className={child ? "h-2.5 w-2.5" : "h-3 w-3"} />}
          imageClassName={`${hasUploadedIcon ? "h-6 w-6" : child ? "h-2.5 w-2.5" : "h-3 w-3"} object-contain`}
        />
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block whitespace-normal break-words text-justify [text-align-last:right] text-sm leading-6 ${active ? "font-extrabold" : "font-semibold"}`}>
          {title}
        </span>
      </span>
      {hasNext ? (
        <span
          className={[
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors duration-200",
            expanded ? "bg-[#005bbb] text-white" : "",
            expanded ? "" : active ? "bg-white text-[#005bbb]" : "bg-slate-50 text-slate-300 group-hover:bg-white group-hover:text-[#005bbb]",
          ].join(" ")}
        >
          <ChevronLeft className={`h-4 w-4 transition-transform duration-300 ${expanded ? '-rotate-90' : ''}`} />
        </span>
      ) : null}
    </button>
  );
}

function StepColumn({ children, depth, empty }) {
  return (
    <section
      className={`relative h-full min-h-0 overflow-hidden overscroll-contain rounded-2xl border border-black/60 bg-white shadow-xl shadow-slate-900/10 ${empty && depth > 0 ? 'menu-empty-column' : ''}`}
      style={{ zIndex: 20 - depth }}
    >
      <div className="h-full space-y-1 overflow-y-auto overflow-x-hidden overscroll-contain p-2">
        {empty ? <span className="sr-only">{empty}</span> : children}
      </div>
    </section>
  );
}

export default function MegaMenu({ onClose, anchorRect }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [cats, setCats] = useState([]);
  const [apiFailed, setApiFailed] = useState(false);
  const [activeL1, setActiveL1] = useState(null);
  const [activeL2, setActiveL2] = useState(null);
  const [activeL3, setActiveL3] = useState(null);
  const [activeL4, setActiveL4] = useState(null);
  const [activeL5, setActiveL5] = useState(null);
  const [mobileStack, setMobileStack] = useState([]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setApiFailed(false);
    api
      .getCategories()
      .then((res) => {
        if (!mounted) return;
        const data = Array.isArray(res?.data) ? res.data : [];
        const l1 = data[0] || null;
        const path = firstPath(l1);
        setCats(data);
        setActiveL1(l1);
        setActiveL2(path.l2);
        setActiveL3(path.l3);
        setActiveL4(path.l4);
        setActiveL5(path.l5);
      })
      .catch(() => {
        if (mounted) {
          setApiFailed(true);
          setCats([]);
        }
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const l1List = useMemo(() => cats || [], [cats]);
  const l2List = useMemo(() => activeL1?.children || [], [activeL1]);
  const l3List = useMemo(() => activeL2?.children || [], [activeL2]);
  const l4List = useMemo(() => activeL3?.children || [], [activeL3]);

  function setL1(cat) {
    const path = firstPath(cat);
    setActiveL1(cat);
    setActiveL2(path.l2);
    setActiveL3(path.l3);
    setActiveL4(path.l4);
    setActiveL5(path.l5);
  }

  function setL2(cat) {
    const path = firstPath(cat);
    setActiveL2(cat);
    setActiveL3(path.l2);
    setActiveL4(path.l3);
    setActiveL5(path.l4);
  }

  function setL3(cat) {
    const path = firstPath(cat);
    setActiveL3(cat);
    setActiveL4(path.l2);
    setActiveL5(path.l3);
  }

  function setL4(cat) {
    setActiveL4(cat);
    setActiveL5(firstChild(cat));
  }

  function goCategory(cat) {
    if (cat?.slug) {
      navigate(navPath(cat));
      onClose?.();
    }
  }

  const mobileCurrent = mobileStack[mobileStack.length - 1] || null;
  const mobileList = mobileCurrent ? mobileCurrent.children || [] : l1List;
  const mobileTitle = mobileCurrent ? mobileCurrent.title : "دسته‌بندی محصولات";

  function mobileOpenLeaf(cat) {
    if (hasChildren(cat)) {
      setMobileStack((s) => [...s, cat]);
      return;
    }
    goCategory(cat);
  }

  const isMobileViewport = window.innerWidth < 1024;
  const panelTop = Math.max(anchorRect?.bottom || 106, 84) + (isMobileViewport ? 12 : 0);
  const panelWidth = Math.min(1188, window.innerWidth);

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 overscroll-contain" style={{ top: panelTop }} dir="rtl">
      <div className="absolute inset-0 bg-slate-950/35 backdrop-blur-[3px]" onClick={() => onClose?.()} />

      <div
        className="absolute bottom-0 right-0 z-10 flex overflow-hidden overscroll-contain bg-slate-100/90 shadow-2xl shadow-slate-900/15 backdrop-blur-sm"
        style={{ top: 0, width: panelWidth, maxWidth: "100vw" }}
      >
        <div className="flex min-h-0 w-full flex-col">
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-white px-4 py-4 sm:px-6">
            <h2 className="text-base font-extrabold text-slate-900">دسته‌بندی محصولات</h2>
            <div className="mr-auto flex items-center gap-2">
              <button type="button" onClick={() => { onClose?.(); navigate('/category-tree'); }} className="inline-flex min-h-10 items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-emerald-700 sm:text-sm">
                <Network className="h-4 w-4 shrink-0" />
                مشاهده نمودار درختی
              </button>
            <button
              onClick={() => onClose?.()}
              className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 transition hover:bg-slate-200 hover:text-slate-900"
              aria-label="بستن"
            >
              <X className="h-5 w-5" />
            </button>
            </div>
          </div>

          {loading && <div className="m-4 rounded-2xl bg-white p-6 text-sm text-slate-500 shadow-xl">در حال بارگذاری...</div>}
          {!loading && apiFailed && <div className="m-4 rounded-2xl bg-white p-6 text-sm text-red-600 shadow-xl">خطا در دریافت دسته‌بندی‌ها</div>}

          {!loading && !apiFailed ? (
            <>
              <div className="hidden min-h-0 flex-1 items-stretch gap-3 overflow-hidden p-4 lg:grid lg:grid-cols-4">
                <StepColumn depth={0} empty={!l1List.length ? "دسته‌ای ثبت نشده است." : ""}>
                  {l1List.map((c) => (
                    <MenuItem key={c.id} category={c} active={activeL1?.id === c.id} title={c.title} hasNext={hasChildren(c)} onEnter={() => setL1(c)} onClick={() => goCategory(c)} />
                  ))}
                </StepColumn>

                <StepColumn depth={1} empty={!activeL1 ? "ابتدا یک دسته اصلی را انتخاب کنید." : !l2List.length ? "زیرمجموعه‌ای ثبت نشده است." : ""}>
                  {l2List.map((c) => (
                    <MenuItem key={c.id} category={c} active={activeL2?.id === c.id} title={c.title} hasNext={hasChildren(c)} onEnter={() => setL2(c)} onClick={() => goCategory(c)} />
                  ))}
                </StepColumn>

                <StepColumn depth={2} empty={!activeL2 ? "برای نمایش محصولات مرتبط، روی ستون قبل بروید." : !l3List.length ? "این دسته مستقیما جدول محصول دارد." : ""}>
                  {l3List.map((c) => (
                    <MenuItem key={c.id} category={c} active={activeL3?.id === c.id} title={c.title} hasNext={hasChildren(c)} onEnter={() => setL3(c)} onClick={() => goCategory(c)} />
                  ))}
                </StepColumn>

                <StepColumn depth={3} empty={!activeL3 ? "برای نمایش محصولات مرتبط، روی ستون قبل بروید." : !l4List.length ? "گروه عمیق‌تری برای این مسیر وجود ندارد." : ""}>
                  {l4List.map((c) => {
                    const children = c.children || [];
                    const showChildren = activeL4?.id === c.id && children.length > 0;
                    return (
                      <div key={c.id} className="space-y-1">
                        <MenuItem category={c} active={activeL4?.id === c.id} expanded={showChildren} title={c.title} hasNext={children.length > 0} onEnter={() => setL4(c)} onClick={() => goCategory(c)} />
                        {showChildren
                          ? children.map((child) => (
                              <MenuItem
                                key={child.id}
                                child
                                wrap
                                category={child}
                                active={activeL5?.id === child.id}
                                title={child.title}
                                hasNext={hasChildren(child)}
                                onEnter={() => setActiveL5(child)}
                                onClick={() => goCategory(child)}
                              />
                            ))
                          : null}
                      </div>
                    );
                  })}
                </StepColumn>
              </div>

              <div className="flex min-h-0 flex-1 flex-col lg:hidden">
                <div className="flex items-center justify-between border-b bg-white px-4 py-3">
                  <div className="min-w-0 whitespace-normal break-words text-sm font-bold text-slate-700">{mobileTitle}</div>
                  {mobileStack.length ? (
                    <button onClick={() => setMobileStack((s) => s.slice(0, -1))} className="flex items-center gap-1 text-sm font-bold text-[#005bbb]">
                      <ChevronRight className="h-4 w-4" />
                      بازگشت
                    </button>
                  ) : (
                    <span />
                  )}
                </div>
                <div className="flex-1 space-y-2 overflow-auto overscroll-contain p-3">
                  {mobileList.length ? (
                    mobileList.map((c) => (
                      <button key={c.id} onClick={() => mobileOpenLeaf(c)} className="flex w-full items-center gap-3 rounded-2xl border bg-white px-4 py-3 text-right shadow-sm">
                        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-400">
                          <CategoryIcon
                            category={c}
                            fallback={<Circle className="h-3 w-3" />}
                            imageClassName={`${getCategoryIconUrl(c) ? "h-6 w-6" : "h-3 w-3"} object-contain`}
                          />
                        </span>
                        <span className="flex-1 font-extrabold text-slate-900">{c.title}</span>
                        {hasChildren(c) ? <ChevronLeft className="h-4 w-4 text-slate-400" /> : null}
                      </button>
                    ))
                  ) : (
                    <div className="text-sm text-slate-500">موردی برای نمایش نیست.</div>
                  )}
                </div>
                <div className="grid grid-cols-1 gap-2 border-t bg-white p-4">
                  {mobileStack.length ? (
                    <Button variant="outline" className="w-full rounded-2xl" onClick={() => setMobileStack((s) => s.slice(0, -1))}>
                      <ChevronRight className="ml-1 h-4 w-4" />
                      برگشت به منوی قبلی
                    </Button>
                  ) : null}
                  <Button variant="outline" className="w-full rounded-2xl" onClick={() => onClose?.()}>
                    بستن
                  </Button>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
